import COISection from './COISection';
import TaxExemptSection from './TaxExemptSection';
import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';

const fmt     = n => '$' + Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtDate = d => { if(!d) return '—'; return new Date(d+'T00:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}); };

const STATUS_COLOR = {
  reserved:  { bg:'#1e3a5f', text:'#60a5fa' },
  started:   { bg:'#1a3a1a', text:'#4ade80' },
  returned:  { bg:'#2a2a0a', text:'#facc15' },
  completed: { bg:'#1a3a1a', text:'#4ade80' },
  cancelled: { bg:'#3b0a0a', text:'#f87171' },
  draft:     { bg:'#2e3150', text:'#94a3b8' },
};
const PAY_COLOR = {
  paid:            { bg:'#1a3a1a', text:'#4ade80' },
  partially_paid:  { bg:'#2a1a0a', text:'#fb923c' },
  payment_due:     { bg:'#3b1a0a', text:'#f87171' },
};

function Badge({ label, map }) {
  const c = map?.[label] || { bg:'#2e3150', text:'#94a3b8' };
  return (
    <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:4, fontSize:10, fontWeight:700,
      background:c.bg, color:c.text, textTransform:'capitalize', whiteSpace:'nowrap' }}>
      {label?.replace(/_/g,' ')||'—'}
    </span>
  );
}

export default function ClientProfilePage({ clientId, onBack, onNewOrder, showFlash, onCheckout, onReturn, onCollect, onOpenOrder }) {
  const [client, setClient]   = useState(null);
  const [orders, setOrders]   = useState([]);
  const [lastActivity, setLastActivity] = useState(null);
  const [quotes, setQuotes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('overview');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]   = useState(false);
  const [note, setNote]       = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [documents, setDocuments]       = useState([]);
  const [docUploading, setDocUploading] = useState(false);
  const [docLabel, setDocLabel]         = useState('');
  const [contracts, setContracts]       = useState([]);
  const [sendingContract, setSendingContract] = useState(false);
  const [contractMsg, setContractMsg]   = useState('');
  const [selectedContract, setSelectedContract] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  useEffect(() => { load(); }, [clientId]);

  async function load() {
    setLoading(true);
    try {
      // clientId may be a numeric ID or a name string (fallback for old orders)
      let id = clientId;
      if (isNaN(Number(clientId))) {
        // It's a name — search for the client first
        const s = await axios.get(`/api/rentals/clients/search?q=${encodeURIComponent(clientId)}`);
        const match = (s.data.clients||[]).find(c => c.name?.toLowerCase() === String(clientId).toLowerCase())
          || s.data.clients?.[0];
        if (!match) { setLoading(false); return; }
        id = match.id;
      }
      const r = await axios.get(`/api/rentals/clients/${id}`);
      setClient(r.data.client);
      setOrders(r.data.orders || []);
      setLastActivity(r.data.last_activity || null);
      setQuotes(r.data.quotes || []);
      setEditForm(r.data.client);
      setNote(r.data.client?.notes || '');
      try {
        const [docsRes, ctRes] = await Promise.all([
          axios.get(`/api/rental-business/clients/${id}/documents`),
          axios.get('/api/rental-business/contracts'),
        ]);
        setDocuments(docsRes.data.documents || []);
        setContracts(ctRes.data.templates || []);
      } catch(e) { /* non-fatal */ }
    } catch(e) { showFlash?.('Failed to load client', 'error'); }
    setLoading(false);
  }

  async function saveEdit() {
    setSaving(true);
    try {
      await axios.put(`/api/rentals/clients/${clientId}`, editForm);
      showFlash?.('Client updated');
      setEditing(false);
      load();
    } catch(e) { showFlash?.(e.response?.data?.error || 'Save failed', 'error'); }
    setSaving(false);
  }

  async function uploadDoc(file) {
    if (!file) return;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', docLabel || file.name);
      await axios.post(`/api/rental-business/clients/${client.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      showFlash?.('Document uploaded');
      setDocLabel('');
      load();
    } catch(e) { showFlash?.(e.response?.data?.error || 'Upload failed', 'error'); }
    setDocUploading(false);
  }

  async function deleteDoc(docId) {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`/api/rental-business/clients/${client.id}/documents/${docId}`);
      showFlash?.('Document deleted');
      load();
    } catch(e) { showFlash?.('Delete failed', 'error'); }
  }

  async function sendContract() {
    if (!selectedContract) return showFlash?.('Select a contract template', 'error');
    setSendingContract(true);
    try {
      await axios.post(`/api/rental-business/clients/${client.id}/send-contract`, { template_id: selectedContract, message: contractMsg });
      showFlash?.('Contract sent!');
      setContractMsg('');
      setSelectedContract('');
    } catch(e) { showFlash?.(e.response?.data?.error || 'Send failed', 'error'); }
    setSendingContract(false);
  }

  async function saveNote() {
    setAddingNote(true);
    try {
      await axios.put(`/api/rentals/clients/${clientId}`, { ...client, notes: note });
      showFlash?.('Note saved');
      load();
    } catch(e) { showFlash?.('Failed to save note', 'error'); }
    setAddingNote(false);
  }

  async function sendPortalInvite() {
    setSendingInvite(true);
    try {
      await axios.post(`/api/rentals/clients/${client.id}/portal-invite`);
      showFlash?.('Portal invite sent');
      setClient(c => ({ ...c, portal_status: 'invited' }));
    } catch(e) { showFlash?.(e.response?.data?.error || 'Failed to send invite', 'error'); }
    setSendingInvite(false);
  }

  // ── computed stats ──────────────────────────────────────────
  const totalSpent    = orders.reduce((s,o) => s + Number(o.total||0), 0);
  const totalPaid     = orders.reduce((s,o) => s + Number(o.amount_paid||0), 0);
  const outstanding   = orders.filter(o => ['payment_due','partially_paid'].includes(o.payment_status)).reduce((s,o) => s + (Number(o.total||0) - Number(o.amount_paid||0)), 0);
  const activeOrders  = orders.filter(o => o.status === 'started').length;
  const avgOrder      = orders.length ? totalSpent / orders.length : 0;
  const lastOrder     = orders[0];   // newest by creation (the endpoint sorts created_at desc, id desc)
  const ACTION_LABEL  = { create: 'Created', reserve: 'Reserved', checkout: 'Checked out', return: 'Returned', cancel: 'Cancelled', manual: 'Status changed (manual)', system: 'Updated' };
  const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return iso; } };
  const activityText = lastActivity
    ? `${ACTION_LABEL[lastActivity.action] || lastActivity.action}${lastActivity.action === 'manual' && lastActivity.to_status ? ` → ${lastActivity.to_status}` : ''}, ${fmtWhen(lastActivity.created_at)}`
    : lastOrder ? `Created, ${fmtWhen(lastOrder.created_at)}` : null;
  const activityOrder = lastActivity ? (lastActivity.order_number || '#' + lastActivity.order_id) : lastOrder ? (lastOrder.order_number || '#' + lastOrder.id) : null;

  // ── styles ──────────────────────────────────────────────────
  const S = {
    page:     { maxWidth: 1100, margin: '0 auto', padding: '0 0 40px' },
    card:     { background:'#252838', border:'1px solid #2e3150', borderRadius:10, padding:20, marginBottom:16 },
    th:       { padding:'8px 12px', textAlign:'left', fontSize:11, color:'#e2e8f0', fontWeight:600, background:'#1e2235', borderBottom:'1px solid #2e3150', textTransform:'uppercase', whiteSpace:'nowrap' },
    td:       { padding:'10px 12px', fontSize:12, color:'#e2e8f0', borderBottom:'1px solid #1e2030', verticalAlign:'middle' },
    inp:      { padding:'7px 10px', borderRadius:6, border:'1px solid #3a3f60', background:'#1e2235', color:'#e2e8f0', fontSize:12, width:'100%', boxSizing:'border-box' },
    label:    { fontSize:11, color:'#e2e8f0', marginBottom:4, textTransform:'uppercase', letterSpacing:'.4px' },
    tabBtn:   (a) => ({ padding:'7px 16px', borderRadius:7, border:'none', cursor:'pointer', fontSize:12, fontWeight:700, background:a?'#f97316':'transparent', color:a?'#fff':'#94a3b8' }),
    metricBox:{ background:'#1e2235', border:'1px solid #2e3150', borderRadius:8, padding:'12px 16px', flex:1 },
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300, color:'#e2e8f0' }}>
      Loading client…
    </div>
  );

  if (!client) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300, color:'#f87171' }}>
      Client not found.
    </div>
  );

  const initials = (client.name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();

  return (
    <div style={S.page}>

      {/* ── Header ── */}
      <div style={{ padding:'16px 0 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={onBack} style={{ background:'none', border:'none', color:'#e2e8f0', cursor:'pointer', fontSize:13, padding:'4px 0' }}>← Clients</button>
          <span style={{ color:'#94a3b8', fontSize:13 }}>/</span>
          <span style={{ fontSize:13, color:'#e2e8f0' }}>{client.name}</span>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {editing ? (
            <>
              <button onClick={saveEdit} disabled={saving} style={{ padding:'7px 18px', borderRadius:7, border:'none', background:'#f97316', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={()=>{ setEditing(false); setEditForm(client); }} style={{ padding:'7px 14px', borderRadius:7, border:'1px solid #3a3f60', background:'transparent', color:'#e2e8f0', fontSize:12, cursor:'pointer' }}>
                Cancel
              </button>
            </>
          ) : (
            <>
              {client.portal_status === 'active' ? (
                <button disabled style={{ padding:'7px 14px', borderRadius:7, border:'1px solid #14532d', background:'#14532d', color:'#4ade80', fontSize:11, fontWeight:700, cursor:'default', opacity:0.8 }}>
                  Portal Active
                </button>
              ) : (
                <button onClick={sendPortalInvite} disabled={sendingInvite || !client.email}
                  style={{ padding:'7px 14px', borderRadius:7, border:'none',
                    background: client.portal_status === 'invited' ? '#422006' : '#f97316',
                    color: client.portal_status === 'invited' ? '#fbbf24' : '#fff',
                    fontSize:11, fontWeight:700, cursor: client.email ? 'pointer' : 'not-allowed',
                    opacity: sendingInvite || !client.email ? 0.6 : 1 }}>
                  {sendingInvite ? 'Sending…' : client.portal_status === 'invited' ? '📧 Resend Invite' : '📧 Send Portal Invite'}
                </button>
              )}
              <button onClick={()=>setEditing(true)} style={{ padding:'7px 16px', borderRadius:7, border:'1px solid #3a3f60', background:'transparent', color:'#e2e8f0', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                ✏️ Edit
              </button>
              <button onClick={()=>onNewOrder?.(client)} style={{ padding:'7px 18px', borderRadius:7, border:'none', background:'#f97316', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                + New Order
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Client Identity ── */}
      <div style={{ ...S.card, display:'flex', gap:20, alignItems:'flex-start', flexWrap:'wrap' }}>
        {/* Avatar */}
        <div style={{ width:64, height:64, borderRadius:'50%', background:'#1e3a5f', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:800, color:'#60a5fa', flexShrink:0 }}>
          {initials}
        </div>

        {/* Info grid */}
        <div style={{ flex:1, display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:'14px 24px' }}>
          {editing && ['name','company','email','phone','address','city','state','zip','dl_number'].map(field => (
            <div key={field}>
              <div style={S.label}>{{name:'Full Name',company:'Company',email:'Email',phone:'Phone',address:'Address',city:'City',state:'State',zip:'ZIP',dl_number:"Driver's License"}[field]}</div>
              <input style={S.inp} value={editForm[field]||''} onChange={e=>setEditForm(f=>({...f,[field]:e.target.value}))} />
            </div>
          ))}
          {editing && (
            <div>
              <div style={S.label}>Account Discount %</div>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <input type="number" style={{...S.inp,width:80}} min={0} max={100} placeholder="0" value={editForm.discount_percent||''} onChange={e=>setEditForm(f=>({...f,discount_percent:e.target.value}))} />
                <span style={{fontSize:11,color:'#e2e8f0'}}>% off portal orders</span>
              </div>
            </div>
          )}
          {!editing && [
            ['Name',     client.name],
            ['Company',  client.company],
            ['Email',    client.email],
            ['Phone',    client.phone],
            ['Address',  client.address],
            ['City',     client.city],
            ['State / ZIP', [client.state, client.zip].filter(Boolean).join(' ')],
            ['DL #',     client.dl_number],
          ].filter(([,v])=>v).map(([label, val]) => (
            <div key={label} style={{ minWidth:0 }}>
              <div style={S.label}>{label}</div>
              <div style={{ fontSize:13, color:'#e2e8f0', fontWeight: label==='Name'?700:400, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {label==='Email' ? <a href={`mailto:${val}`} style={{ color:'#60a5fa', textDecoration:'none' }} title={val}>{val}</a>
                 : label==='Phone' ? <a href={`tel:${val}`} style={{ color:'#60a5fa', textDecoration:'none' }}>{val}</a>
                 : val}
              </div>
            </div>
          ))}
          {!editing && client.discount_percent > 0 && (
            <div>
              <div style={S.label}>Account Discount</div>
              <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:4, fontSize:12, fontWeight:700, background:'#1a2a0a', color:'#f97316' }}>
                🏷 {client.discount_percent}% off portal orders
              </span>
            </div>
          )}

          {/* Insurance badge */}
          {!editing && client.insurance_cert && (
            <div>
              <div style={S.label}>Insurance</div>
              <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:4, fontSize:10, fontWeight:700, background:'#1a3a1a', color:'#4ade80' }}>
                ✓ Cert on file {client.insurance_discount > 0 ? `— ${client.insurance_discount}% disc.` : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Metric Strip ── */}
      <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        {[
          { label:'Total Orders',   val: orders.length,         color:'#e2e8f0' },
          { label:'Total Billed',   val: fmt(totalSpent),        color:'#f97316' },
          { label:'Total Paid',     val: fmt(totalPaid),         color:'#34d399' },
          { label:'Outstanding',    val: fmt(outstanding),       color: outstanding > 0 ? '#f87171' : '#94a3b8' },
          { label:'Avg Order',      val: fmt(avgOrder),          color:'#e2e8f0' },
          { label:'Active Now',     val: activeOrders,           color: activeOrders > 0 ? '#60a5fa' : '#94a3b8' },
        ].map(m => (
          <div key={m.label} style={S.metricBox}>
            <div style={{ fontSize:20, fontWeight:700, color:m.color }}>{m.val}</div>
            <div style={{ fontSize:10, color:'#e2e8f0', textTransform:'uppercase', letterSpacing:'.4px', marginTop:2 }}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display:'flex', gap:4, background:'#1e2235', borderRadius:9, padding:4, marginBottom:16, width:'fit-content' }}>
        {[['overview','📋 Overview'],['orders','📦 Orders'],['equipment','🔧 Equipment'],['quotes','💬 Quotes'],['documents','📄 Documents'],['notes','📝 Notes'],['coi','🛡️ Insurance'],['tax','🧾 Tax']].map(([t,l])=>(
          <button key={t} style={S.tabBtn(tab===t)} onClick={()=>setTab(t)}>{l}</button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          {/* Recent Orders */}
          <div style={S.card}>
            <div style={{ fontWeight:700, color:'#e2e8f0', marginBottom:14, display:'flex', justifyContent:'space-between' }}>
              Recent Orders
              <span style={{ fontSize:11, color:'#e2e8f0' }}>{orders.length} total</span>
            </div>
            {orders.length === 0 && <div style={{ color:'#94a3b8', fontSize:12, textAlign:'center', padding:20 }}>No orders yet</div>}
            {orders.slice(0,5).map(o => (
              <div key={o.id} style={{ padding:'10px 0', borderBottom:'1px solid #1e2030', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div data-testid="recent-order" data-order-id={o.id} style={{ fontSize:12, fontWeight:600, color:'#e2e8f0', marginBottom:3 }}>{o.order_number || 'Order #' + o.id}{o.order_number && <span style={{ color:'#64748b', fontWeight:400 }}> · #{o.id}</span>}</div>
                  <div style={{ fontSize:11, color:'#e2e8f0' }}>{fmtDate(o.from_date)} → {fmtDate(o.to_date)}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#f97316', marginBottom:4 }}>{fmt(o.total)}</div>
                  <Badge label={o.status} map={STATUS_COLOR} />
                </div>
              </div>
            ))}
          </div>

          {/* Quick Info */}
          <div>
            {/* Last activity */}
            {lastOrder && (
              <div style={{ ...S.card, marginBottom:16 }}>
                <div style={{ fontWeight:700, color:'#e2e8f0', marginBottom:12 }}>Last Activity</div>
                <div data-testid="last-activity" style={{ fontSize:13, fontWeight:700, color:'#f1f5f9', marginBottom:8 }}>{activityText}</div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                  <span style={{ fontSize:12, color:'#e2e8f0' }}>Order</span>
                  <span data-testid="last-activity-order" style={{ fontSize:12, color:'#e2e8f0' }}>{activityOrder}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                  <span style={{ fontSize:12, color:'#e2e8f0' }}>Amount</span>
                  <span style={{ fontSize:13, fontWeight:700, color:'#f97316' }}>{fmt(lastOrder.total)}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, color:'#e2e8f0' }}>Status</span>
                  <Badge label={lastOrder.status} map={STATUS_COLOR} />
                </div>
              </div>
            )}

            {/* Notes preview */}
            <div style={S.card}>
              <div style={{ fontWeight:700, color:'#e2e8f0', marginBottom:10, display:'flex', justifyContent:'space-between' }}>
                Notes
                <button onClick={()=>setTab('notes')} style={{ background:'none', border:'none', color:'#e2e8f0', cursor:'pointer', fontSize:11 }}>Edit →</button>
              </div>
              <div style={{ fontSize:12, color: client.notes ? '#94a3b8' : '#3a3f60', fontStyle: client.notes?'normal':'italic', lineHeight:1.6, whiteSpace:'pre-wrap' }}>
                {client.notes || 'No notes yet — click Edit to add'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── EQUIPMENT TAB ── */}
      {tab === 'equipment' && (
        <div style={S.card}>
          <div style={{fontWeight:700,color:'#e2e8f0',marginBottom:16,fontSize:14}}>Equipment Rented by This Client</div>
          {(() => {
            // Build map of item_name -> list of orders
            const equipMap = {};
            orders.forEach(o => {
              (o.rental_order_items||[]).forEach(li => {
                const name = li.item_name || 'Unknown';
                if (!equipMap[name]) equipMap[name] = [];
                equipMap[name].push(o);
              });
            });
            const entries = Object.entries(equipMap).sort((a,b) => b[1].length - a[1].length);
            if (entries.length === 0) return <div style={{textAlign:'center',padding:40,color:'#94a3b8',fontSize:13}}>No equipment history yet</div>;
            return entries.map(([name, orderList]) => (
              <div key={name} style={{marginBottom:16,borderBottom:'1px solid #2e3150',paddingBottom:16}}>
                <div style={{fontWeight:700,fontSize:13,color:'#e2e8f0',marginBottom:8,display:'flex',alignItems:'center',gap:8}}>
                  🔧 {name}
                  <span style={{fontSize:10,color:'#94a3b8',fontWeight:400}}>({orderList.length} rental{orderList.length>1?'s':''})</span>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:4}}>
                  {orderList.map(o => (
                    <div key={o.id} onClick={()=>onOpenOrder&&onOpenOrder(o.id)}
                      style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 10px',background:'#1a1c2e',borderRadius:6,cursor:onOpenOrder?'pointer':'default',border:'1px solid #2e3150'}}>
                      <span style={{fontSize:11,fontWeight:700,color:'#60a5fa',fontFamily:'monospace'}}>{o.order_number||'#'+o.id}</span>
                      <span style={{fontSize:11,color:'#94a3b8'}}>{fmtDate(o.from_date)} → {fmtDate(o.to_date)}</span>
                      <span style={{fontSize:11,fontWeight:700,color:'#f97316'}}>{fmt(o.total)}</span>
                      <span style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:'#2e3150',color:'#94a3b8',textTransform:'capitalize'}}>{o.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* ── ORDERS TAB ── */}
      {tab === 'orders' && (
        <div style={S.card}>
          {orders.length === 0 ? (
            <div style={{ textAlign:'center', padding:40, color:'#94a3b8', fontSize:13 }}>No orders yet</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                {['Order #','Items','From','To','Total','Paid','Status','Payment',''].map(h=>(
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {orders.map(o => {
                  const itemNames = (o.rental_order_items||[]).map(i=>i.item_name).join(', ');
                  return (
                    <tr key={o.id} style={{ background:'transparent', cursor: onOpenOrder ? 'pointer' : 'default' }}
                      onMouseEnter={e=>e.currentTarget.style.background='#1e2235'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                      onClick={() => onOpenOrder && onOpenOrder(o.id)}>
                      <td style={S.td}><span data-testid="orders-tab-number" data-order-id={o.id} style={{ fontWeight:700, color:'#60a5fa', fontFamily:'monospace' }}>{o.order_number || '#' + o.id}</span>{o.order_number && <span style={{ fontSize:10, color:'#64748b' }}> #{o.id}</span>}</td>
                      <td style={{ ...S.td, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#e2e8f0' }}>{itemNames || '—'}</td>
                      <td style={S.td}>{fmtDate(o.from_date)}</td>
                      <td style={S.td}>{fmtDate(o.to_date)}</td>
                      <td style={{ ...S.td, fontWeight:700, color:'#f97316' }}>{fmt(o.total)}</td>
                      <td style={{ ...S.td, color:'#34d399' }}>{fmt(o.amount_paid)}</td>
                      <td style={S.td}><Badge label={o.status} map={STATUS_COLOR} /></td>
                      <td style={S.td}><Badge label={o.payment_status} map={PAY_COLOR} /></td>
                      <td style={S.td}>
                        <div style={{display:'flex',gap:4}}>
                          {o.status==='reserved' && <button style={{padding:'2px 8px',borderRadius:4,border:'none',background:'#1a2e1a',color:'#4ade80',fontSize:10,fontWeight:700,cursor:'pointer'}} data-testid="client-checkout" data-order-id={o.id} onClick={()=>onCheckout&&onCheckout(o)}>▶ Check Out</button>}
                          {o.status==='started' && <button style={{padding:'2px 8px',borderRadius:4,border:'none',background:'#0a1a2e',color:'#60a5fa',fontSize:10,fontWeight:700,cursor:'pointer'}} onClick={()=>onReturn&&onReturn(o)}>↩ Return</button>}
                          {['started','reserved'].includes(o.status) && o.payment_status!=='paid' && <button style={{padding:'2px 8px',borderRadius:4,border:'none',background:'#14532d',color:'#4ade80',fontSize:10,fontWeight:700,cursor:'pointer'}} onClick={()=>onCollect&&onCollect(o)}>💰 Collect</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background:'#1e2235' }}>
                  <td colSpan={4} style={{ ...S.td, fontWeight:700, color:'#e2e8f0' }}>Totals</td>
                  <td style={{ ...S.td, fontWeight:700, color:'#f97316' }}>{fmt(totalSpent)}</td>
                  <td style={{ ...S.td, fontWeight:700, color:'#34d399' }}>{fmt(totalPaid)}</td>
                  <td colSpan={2} style={S.td} />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* ── QUOTES TAB ── */}
      {tab === 'quotes' && (
        <div style={S.card}>
          {quotes.length === 0 ? (
            <div style={{ textAlign:'center', padding:40, color:'#94a3b8', fontSize:13 }}>No quotes for this client</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                {['Quote #','From','To','Items','Total','Status','Created'].map(h=>(
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {quotes.map(q => (
                  <tr key={q.id}
                    onMouseEnter={e=>e.currentTarget.style.background='#1e2235'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={S.td}><span style={{ fontWeight:700, color:'#60a5fa' }}>#{q.id}</span></td>
                    <td style={S.td}>{fmtDate(q.from_date)}</td>
                    <td style={S.td}>{fmtDate(q.to_date)}</td>
                    <td style={{ ...S.td, color:'#e2e8f0' }}>{(q.items||[]).length} item(s)</td>
                    <td style={{ ...S.td, fontWeight:700, color:'#f97316' }}>{fmt(q.total)}</td>
                    <td style={S.td}><Badge label={q.status} map={STATUS_COLOR} /></td>
                    <td style={{ ...S.td, color:'#e2e8f0' }}>{q.created_at ? new Date(q.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── DOCUMENTS TAB ── */}
      {tab === 'documents' && (
        <div>
          {/* Upload Document */}
          <div style={S.card}>
            <div style={{ fontWeight:700, color:'#e2e8f0', marginBottom:14 }}>Upload Document</div>
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <input type="text" placeholder="Label (optional)" value={docLabel} onChange={e=>setDocLabel(e.target.value)}
                style={{ flex:1, minWidth:140, padding:'7px 10px', borderRadius:6, border:'1px solid #3a3f60', background:'#1e2235', color:'#e2e8f0', fontSize:12 }} />
              <label style={{ padding:'7px 16px', borderRadius:6, background:'#1e3a5f', color:'#60a5fa', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                {docUploading ? 'Uploading...' : 'Choose File'}
                <input type="file" style={{ display:'none' }} disabled={docUploading} onChange={e=>uploadDoc(e.target.files[0])} />
              </label>
            </div>
          </div>
          {/* Uploaded Documents */}
          <div style={S.card}>
            <div style={{ fontWeight:700, color:'#e2e8f0', marginBottom:14 }}>Client Documents</div>
            {documents.length === 0 ? (
              <div style={{ textAlign:'center', padding:24, color:'#e2e8f0', fontSize:12 }}>No documents uploaded yet</div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>{['Label','File','Uploaded',''].map(h=>(<th key={h} style={S.th}>{h}</th>))}</tr></thead>
                <tbody>
                  {documents.map(d => (
                    <tr key={d.id} onMouseEnter={e=>e.currentTarget.style.background='#1e2235'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{ ...S.td, fontWeight:600, color:'#e2e8f0' }}>{d.label || d.file_name}</td>
                      <td style={{ ...S.td, color:'#e2e8f0', fontSize:11 }}>{d.file_name}</td>
                      <td style={{ ...S.td, color:'#e2e8f0', fontSize:11 }}>{new Date(d.created_at).toLocaleDateString()}</td>
                      <td style={S.td}>
                        <div style={{ display:'flex', gap:6 }}>
                          <a href={d.file_url} target="_blank" rel="noopener noreferrer"
                            style={{ padding:'3px 10px', borderRadius:5, background:'#1e3a5f', color:'#60a5fa', fontSize:11, fontWeight:700, textDecoration:'none' }}>View</a>
                          <button onClick={()=>deleteDoc(d.id)}
                            style={{ padding:'3px 10px', borderRadius:5, background:'#3b0a0a', color:'#f87171', fontSize:11, fontWeight:700, border:'none', cursor:'pointer' }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* Send Contract */}
          {contracts.length > 0 && (
            <div style={S.card}>
              <div style={{ fontWeight:700, color:'#e2e8f0', marginBottom:14 }}>Send Contract to Client</div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <select value={selectedContract} onChange={e=>setSelectedContract(e.target.value)}
                  style={{ padding:'7px 10px', borderRadius:6, border:'1px solid #3a3f60', background:'#1e2235', color:'#e2e8f0', fontSize:12 }}>
                  <option value="">Select a contract template</option>
                  {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <textarea placeholder="Optional message to include in email..." value={contractMsg} onChange={e=>setContractMsg(e.target.value)} rows={2}
                  style={{ padding:'7px 10px', borderRadius:6, border:'1px solid #3a3f60', background:'#1e2235', color:'#e2e8f0', fontSize:12, resize:'vertical' }} />
                <button onClick={sendContract} disabled={sendingContract || !selectedContract}
                  style={{ alignSelf:'flex-start', padding:'8px 20px', borderRadius:6, background:sendingContract?'#3a3f60':'#f97316', color:'#fff', fontWeight:700, fontSize:12, border:'none', cursor:'pointer' }}>
                  {sendingContract ? 'Sending...' : 'Send Contract'}
                </button>
              </div>
            </div>
          )}
          {/* Signed Contracts from Orders */}
          <div style={S.card}>
            <div style={{ fontWeight:700, color:'#e2e8f0', marginBottom:14 }}>Signed Agreements (from Orders)</div>
            {orders.filter(o => o.contract_pdf_path || o.signed_at).length === 0 ? (
              <div style={{ textAlign:'center', padding:24, color:'#e2e8f0', fontSize:12 }}>No signed contracts yet</div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>{['Order #','Signed By','Signed At',''].map(h=>(<th key={h} style={S.th}>{h}</th>))}</tr></thead>
                <tbody>
                  {orders.filter(o => o.contract_pdf_path || o.signed_at).map(o => (
                    <tr key={o.id} onMouseEnter={e=>e.currentTarget.style.background='#1e2235'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={S.td}><span style={{ fontWeight:700, color:'#60a5fa' }}>#{o.id}</span></td>
                      <td style={{ ...S.td, color:'#e2e8f0' }}>{o.signer_name || client.name}</td>
                      <td style={S.td}>{o.signed_at ? new Date(o.signed_at).toLocaleDateString() : '-'}</td>
                      <td style={S.td}>
                        {o.contract_pdf_path ? (
                          <a href="#contract" onClick={async (e) => { e.preventDefault(); try { const r = await axios.get(`/api/rentals/orders/${o.id}/contract-file`); window.open(r.data.url, '_blank', 'noopener'); } catch (err) { alert(err.response?.data?.error || 'Could not open the contract'); } }} rel="noopener noreferrer"
                            style={{ padding:'3px 10px', borderRadius:5, background:'#1e3a5f', color:'#60a5fa', fontSize:11, fontWeight:700, textDecoration:'none' }}>View PDF</a>
                        ) : <span style={{ fontSize:11, color:'#e2e8f0' }}>No PDF</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* Payment Records */}
          <div style={S.card}>
            <div style={{ fontWeight:700, color:'#e2e8f0', marginBottom:14 }}>Payment Records</div>
            {orders.filter(o => Number(o.amount_paid) > 0).length === 0 ? (
              <div style={{ textAlign:'center', padding:30, color:'#94a3b8', fontSize:12 }}>No payments recorded</div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  {['Order #','Date','Total','Paid','Balance','Status'].map(h=>(<th key={h} style={S.th}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {orders.filter(o => Number(o.amount_paid) > 0 || Number(o.total) > 0).map(o => {
                    const balance = Math.max(0, Number(o.total||0) - Number(o.amount_paid||0));
                    return (
                      <tr key={o.id} onMouseEnter={e=>e.currentTarget.style.background='#1e2235'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={S.td}><span style={{ fontWeight:700, color:'#60a5fa' }}>#{o.id}</span></td>
                        <td style={S.td}>{fmtDate(o.from_date)}</td>
                        <td style={{ ...S.td, fontWeight:700, color:'#f97316' }}>{fmt(o.total)}</td>
                        <td style={{ ...S.td, color:'#34d399' }}>{fmt(o.amount_paid)}</td>
                        <td style={{ ...S.td, color: balance > 0 ? '#f87171' : '#94a3b8' }}>{fmt(balance)}</td>
                        <td style={S.td}><Badge label={o.payment_status} map={PAY_COLOR} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── NOTES TAB ── */}
      {tab === 'coi' && (
        <COISection clientId={client?.id} />
      )}
      {tab === 'tax' && (
        <TaxExemptSection clientId={client?.id} />
      )}
      {tab === 'notes' && (
        <div style={S.card}>
          <div style={{ fontWeight:700, color:'#e2e8f0', marginBottom:12 }}>📝 Client Notes</div>
          <textarea
            value={note}
            onChange={e=>setNote(e.target.value)}
            placeholder="Add notes about this client — payment habits, equipment preferences, special requirements, contact preferences…"
            style={{ ...S.inp, height:180, resize:'vertical', lineHeight:1.6, fontFamily:'inherit' }}
          />
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:10 }}>
            <button onClick={saveNote} disabled={addingNote}
              style={{ padding:'7px 20px', borderRadius:7, border:'none', background:'#f97316', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
              {addingNote ? 'Saving…' : 'Save Note'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
